import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGlobalError } from 'hooks/useGlobalError';
import { useCurrentUser } from 'providers/cache/cachedDataHooks';
import { userService } from 'services/user/userService';
import { convertToPhoneDto } from 'utils/phoneFormatter';

type StatusType = 'notExecuted' | 'inProgress' | 'completed' | 'failed';

export const useUpdateAndVerifyPhone = () => {
  const { t } = useTranslation('account');
  const [phoneUpdateStatus, setPhoneUpdateStatus] = useState<StatusType>('notExecuted');
  const [phoneUpdateError, setPhoneUpdateError] = useState('');
  const [verifyPhoneStatus, setVerifyPhoneStatus] = useState<StatusType>('notExecuted');

  const { unknownError } = useGlobalError();
  const { refreshCurrentUser } = useCurrentUser();

  const updatePhone = async (phoneNumber: string) => {
    setPhoneUpdateStatus('inProgress');
    try {
      await userService.updatePhoneNumber(convertToPhoneDto(phoneNumber, true));
      setPhoneUpdateStatus('completed');
    } catch (error: any) {
      setPhoneUpdateStatus('failed');
      if (error.response?.status === 409) {
        setPhoneUpdateError(t('verify-phone.number-in-use'));
      } else {
        unknownError();
      }
    }
  };

  const verifyPhone = async (pin: string) => {
    setVerifyPhoneStatus('inProgress');
    try {
      await userService.confirmPhone(pin);
      refreshCurrentUser(() => setVerifyPhoneStatus('completed'));
    } catch (error: any) {
      setVerifyPhoneStatus('failed');
    }
  };

  const resendCode = async () => {
    try {
      setVerifyPhoneStatus('notExecuted');
      await userService.resendPhonePin();
    } catch (error: any) {
      unknownError();
    }
  };

  const resetForm = () => {
    setPhoneUpdateStatus('notExecuted');
    setPhoneUpdateError('');
    setVerifyPhoneStatus('notExecuted');
  };

  return { updatePhone, phoneUpdateStatus, phoneUpdateError, verifyPhone, verifyPhoneStatus, resendCode, resetForm };
};
